import React, { useEffect } from 'react';
import HOW_IT_WORKS from './guide/howItWorks';

/**
 * 2days 사용자 가이드 (/2days/guide) — A6 How it works.
 * 시나리오 창의 "user guide" 링크가 새 탭으로 연다. 로그인 없이 볼 수 있다.
 */

/** "**굵게**" 를 <strong> 으로 */
const Rich = ({ text }) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      // eslint-disable-next-line react/no-array-index-key
      <strong key={i} className='font-medium text-text-primary'>
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    ),
  );

const LIST = 'flex flex-col gap-2 pl-5 text-[15px] leading-[170%] text-text-secondary';

/** 목록 한 줄 — [글, [하위…]] 이면 하위 목록을 붙인다 */
const Item = ({ item }) =>
  Array.isArray(item) ? (
    <li>
      <Rich text={item[0]} />
      <ul className={`${LIST} mt-2 list-[circle]`}>
        {item[1].map((sub) => (
          <li key={sub}>
            <Rich text={sub} />
          </li>
        ))}
      </ul>
    </li>
  ) : (
    <li>
      <Rich text={item} />
    </li>
  );

const Table = ({ head, rows }) => (
  <div className='overflow-x-auto rounded-xl border border-green-deep03/[0.12]'>
    <table className='w-full min-w-[480px] border-collapse text-left text-[14px] leading-[160%]'>
      <thead className='bg-green-deep03/[0.04]'>
        <tr>
          {head.map((h) => (
            <th key={h} className='px-4 py-2.5 font-medium text-text-primary'>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[0]} className='border-t border-green-deep03/[0.12] align-top'>
            {row.map((cell, i) => (
              <td
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={
                  i === 0 ? 'px-4 py-3 text-text-primary' : 'px-4 py-3 text-text-secondary'
                }
              >
                <Rich text={cell} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Block = ({ block }) => {
  if (block.p) {
    return (
      <p className='text-[15px] leading-[170%] text-text-secondary'>
        <Rich text={block.p} />
      </p>
    );
  }
  if (block.ul) {
    return (
      <ul className={`${LIST} list-disc`}>
        {block.ul.map((item) => (
          <Item key={Array.isArray(item) ? item[0] : item} item={item} />
        ))}
      </ul>
    );
  }
  if (block.ol) {
    return (
      <ol className={`${LIST} list-decimal`}>
        {block.ol.map((item) => (
          <Item key={item} item={item} />
        ))}
      </ol>
    );
  }
  return <Table {...block.table} />;
};

const TwoDaysGuide = () => {
  const { title, version, intro, sections } = HOW_IT_WORKS;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div lang='en' className='min-h-screen bg-grayscale-gray00'>
      <div className='sticky top-0 z-10 flex justify-center border-b border-green-deep03/[0.08] bg-grayscale-gray00/95 backdrop-blur'>
        <div className='flex w-full max-w-[768px] items-center justify-between gap-3 px-6 py-3'>
          <span className='text-[13px] font-medium text-green-deep03'>2days project</span>
          <span className='text-[12px] text-text-secondary'>{version}</span>
        </div>
      </div>
      <main className='mx-auto flex w-full max-w-[768px] flex-col gap-10 px-6 pb-20 pt-10'>
        <header className='flex flex-col gap-4'>
          <h1 className='text-[28px] font-medium leading-[125%] tracking-[-0.01em] text-text-primary sm:text-[34px]'>
            {title}
          </h1>
          <p className='rounded-xl bg-surface-green10 px-4 py-3 text-[15px] leading-[160%] text-text-primary'>
            <Rich text={intro} />
          </p>
        </header>
        {sections.map((s) => (
          <section key={s.heading} className='flex flex-col gap-4'>
            <h2 className='text-[20px] font-medium leading-[140%] text-text-primary'>
              {s.heading}
            </h2>
            {s.blocks.map((b, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Block key={i} block={b} />
            ))}
          </section>
        ))}
      </main>
    </div>
  );
};

export default TwoDaysGuide;
